<template>
  <v-app>
    <component
      :is="layoutComponent"
      v-if="maintenanceLoaded && !maintenanceMode"
    >
      <router-view />
    </component>
    <MaintenanceView v-else-if="maintenanceLoaded && maintenanceMode" />
    <CircularLoader v-if="!maintenanceLoaded || appLoading" />

    <!-- <v-btn @click="createEmailTemplate">Template</v-btn> -->
    <OfflineWarning v-if="offline" />
    <v-dialog persistent v-model="dialogSessionWarning" max-width="505px">
      <v-card>
        <v-card-title class="justify-center" style="word-break: break-word"
          >Juffrou Anri is open in another window. Click "Use Here" to use Miss
          Anri's ClassNetwork in this window.</v-card-title
        >
        <v-card-actions>
          <v-btn
            depressed
            color="secondary"
            class="black--text"
            :disabled="!datastoreReady"
            :loading="!datastoreReady"
            @click="sessionWarningClose"
            >Close</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn depressed color="primary" @click="sessionWarningUseHere"
            >Use Here</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="justBoughtSnackbar"
      timeout="8000"
      color="primary"
      multi-line
      bottom
      right
    >
      <div class="snackbar-click-wrapper" @click="goToJustBoughtProduct">
        <v-row align="center" no-gutters>
          <v-col cols="auto" class="pr-4">
            <v-img
              :src="justBoughtThumbnailUrl"
              width="64"
              height="64"
              contain
              class="rounded"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular
                    indeterminate
                    color="grey lighten-1"
                  ></v-progress-circular>
                </v-row>
              </template>
            </v-img>
          </v-col>
          <v-col>
            <div class="text-h6 font-weight-bold">Someone just bought!</div>
            <div class="font-weight-bold">
              {{ justBoughtProduct.title || "New Product" }}
            </div>
          </v-col>
        </v-row>
      </div>
    </v-snackbar>
  </v-app>
</template>

<script>
import Vue from "vue";
import store from "@/store";
import { mapState, mapGetters } from "vuex";
import CircularLoader from "@/components/global-components/CircularLoader.vue";
import OfflineWarning from "@/components/global-components/OfflineWarning.vue";
import NetworkStatus from "@/mixins/networkStatus.js";
import { DataStore, Auth, API, Storage } from "aws-amplify";
import { Announcement, TeacherProductsOrder } from "@/models";
import AWS from "aws-sdk";
import _ from "lodash";
// import { listTeacherProfiles } from "@/graphql/queries";
import { reconfigureDatastore } from "@/plugins/datastore/config.js";
import router from "@/router";
import {
  teacherProfilesByOwner,
  teacherProductsOrdersByTeacherprofileID,
  appConfigsByKey,
} from "@/graphql/queries";
import {
  createTeacherProfile,
  updateTeacherProfile,
  deleteTeacherProfile,
  updateTeacherProductsOrder,
} from "@/graphql/mutations";
import {
  onCreateTeacherProfile,
  onUpdateTeacherProfile,
  onDeleteTeacherProfile,
  onCreateTeacherProductsOrder,
  onUpdateTeacherProductsOrder,
  onDeleteTeacherProductsOrder,
} from "@/graphql/subscriptions";
import MaintenanceView from "@/views/MaintenanceView.vue";
import justBoughtProductsMixin from "@/mixins/justBoughtProductsMixin.js";
import TeacherProfileAppsyncMixin from "@/mixins/TeacherProfileAppsyncMixin.js";
import SubscriptionAccessAppsyncMixin from "@/mixins/SubscriptionAccessAppsyncMixin.js";

export default Vue.extend({
  name: "App",
  components: { CircularLoader, OfflineWarning, MaintenanceView },
  mixins: [
    NetworkStatus,
    justBoughtProductsMixin,
    TeacherProfileAppsyncMixin,
    SubscriptionAccessAppsyncMixin,
  ],
  data: () => ({
    initiallySynced: false,
    initiallyLoaded: false,
    createdNewTeacherProfile: false,
    // teacherProfileSubscription: null,
    // announcementSubscription: null,
    // teacherProductsOrderSubscription: null,
    // syncedTeacherProfileModel: null,

    justBoughtSnackbar: false,
    justBoughtThumbnailUrl: "",
    justBoughtProduct: "",

    // teacherProfileSubscription: null,
    teacherProductsOrderSubscription: null, // Note: Keeping for consistency, but we'll use separate create/update/delete subs below

    // onCreateTeacherProfileSub: null,
    // onDeleteTeacherProfileSub: null,

    teacherProductsOrderCreateSub: null,
    teacherProductsOrderUpdateSub: null,
    teacherProductsOrderDeleteSub: null,

    updatedSmartProductSort: false, // Add this if not already present (used in logic)

    dialogSessionWarning: false,
    newTeacherSessionToken: "",
    changedDatastorePredicate: false,
    previouslySyncedTeacherProfile: false,
    // dataStoreConfig: "",
  }),

  computed: {
    layoutComponent() {
      return this.$route && this.$route.meta && this.$route.meta.layout;
    },

    ...mapGetters(["subscriptionExists", "maintenanceMode"]),

    ...mapState({
      offline: (state) => !state.isOnline,
      fetchedUserGroups: (state) => state.fetchedUserGroups,
      userGroups: (state) => state.userGroups,
      userSignedIn: (state) => state.userSignedIn,
      userEmail: (state) => state.userEmail,
      appLoading: (state) => state.appLoading,
      syncedTeacherProfileModel: (state) => state.syncedTeacherProfileModel,
      teacherProfileSchoolID: (state) => state.teacherProfileSchoolID,
      teacherProfileProvince: (state) => state.teacherProfileProvince,
      teacherProfileID: (state) => state.teacherProfileID,
      teacherProfilePayfastSubscriptionToken: (state) =>
        state.teacherProfilePayfastSubscriptionToken,
      teacherProfilePayfastSubscriptionResponse: (state) =>
        state.teacherProfilePayfastSubscriptionResponse,
      teacherProfileAppSyncVersion: (state) =>
        state.teacherProfileAppSyncVersion,
      changingDatastoreConfig: (state) => state.changingDatastoreConfig,
      datastoreReady: (state) => state.datastoreReady,
      startedDatastoreSync: (state) => state.startedDatastoreSync,
      syncedTeacherProfile: (state) => state.syncedTeacherProfile,
      maintenanceLoaded: (state) => state.maintenanceLoaded,
    }),

    authMode() {
      return this.userSignedIn ? "AMAZON_COGNITO_USER_POOLS" : "AWS_IAM";
    },

    teacherProfileCreatable() {
      // console.log(
      //   "teacherProfileCreatable:",
      //   this.userSignedIn,
      //   !this.syncedTeacherProfileModel,
      //   !router.currentRoute.path.includes("/admin"),
      //   !this.createdNewTeacherProfile,
      //   this.syncedTeacherProfile,
      //   this.datastoreReady,
      //   !this.changingDatastoreConfig
      // );
      return (
        this.userSignedIn &&
        !this.syncedTeacherProfileModel &&
        !router.currentRoute.path.includes("/admin") &&
        !this.createdNewTeacherProfile &&
        this.syncedTeacherProfile &&
        this.datastoreReady &&
        !this.changingDatastoreConfig
      );
    },
  },

  async mounted() {
    try {
      // console.log("app.vue mounted");

      const user = await Auth.currentAuthenticatedUser().catch(() => {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
      });

      store.commit("storeUserSignedIn", user ? true : false);
      store.commit("storeUserEmail", user ? user.attributes.email : "");
      
      if (user) {
        this.syncSubscriptionAccess();
      }
      
      store.commit("loadGuestCart");
      store.dispatch("syncAdMetaToCart"); // guests get stamped immediately

      // await this.$store.dispatch("fetchAppConfig", "maintenance");

      if (this.maintenanceMode) {
        return;
      }

      if (user && !router.currentRoute.path.includes("/admin")) {
        store.commit("changeDatastorePredicates", [
          // {
          //   model: "TeacherProfile",
          //   predicate: "Teacher",
          // },
          {
            model: "Announcement",
            predicate: "Teacher",
          },
          // {
          //   model: "TeacherProductsOrder",
          //   predicate: "Teacher",
          // },
        ]);

        // this.$nextTick(() => {
        if (!this.changingDatastoreConfig) {
          // console.log("mounted syncTeacherProfile");
          this.syncTeacherProfile();
        }
        // });
      }

      // Logged-out (guest) users: start the subscription immediately with no
      // category filter so they still see the "someone just bought" snackbar.
      if (!user) {
        this.syncJustBoughtProducts();
      }

      // console.log("user:", user);
    } catch (error) {
      console.log(error);
    } finally {
      if (!this.initiallyLoaded) {
        this.initiallyLoaded = true;
      }
    }
  },

  watch: {
    userSignedIn: {
      handler(val) {
        if (val && !this.subscriptionAccessEmail) {
          this.syncSubscriptionAccess();
        }
      },
    },

    syncedTeacherProfileModel: {
      handler: async function (val) {
          // console.log("syncedTeacherProfileModel:", val);

          // if (val && !this.initiallySynced && !this.changingDatastoreConfig) {
          //   // console.log("app.vue watch changingDatastoreConfig syncData");
          //   this.syncData();
          //   this.initiallySynced = true;
          // }

        if (val && this.fetchedUserGroups) {
          // console.log("syncedTeacherProfileModel checkTeacherRole:", val);
          this.checkTeacherRole();
        }
      },
      deep: true,
    },

    fetchedUserGroups: {
      handler: async function (val) {
        if (val && this.syncedTeacherProfileModel) {
          // console.log("fetchedUserGroups checkTeacherRole:", val);
          this.checkTeacherRole();
        }
      },
      deep: true,
    },

    datastoreReady: {
      handler: async function (val) {
        // console.log(
        //   "app.vue watch - datastoreReady",
        //   val,
        //   "this.changingDatastoreConfig:",
        //   this.changingDatastoreConfig,
        //   "this.changedDatastorePredicate:",
        //   this.changedDatastorePredicate,
        // );
        if (val === true && !this.changingDatastoreConfig) {
          if (this.changedDatastorePredicate) {
            // console.log("app.vue watch datastoreReady - reconfigureDatastore");
            await reconfigureDatastore();
            this.changedDatastorePredicate = false;
            return;
          }

          if (this.teacherProfileCreatable) {
            // console.log("app.vue watch datastoreReady - createTeacherProfile");
            this.createTeacherProfile();
          }
        }
      },
      // deep: true,
    },

    changingDatastoreConfig: {
      handler(newVal, oldVal) {
        // console.log("app.vue watch changingDatastoreConfig", newVal, oldVal);

        if (newVal === true || !this.userSignedIn) {
          return;
        }

        if (
          // !this.teacherProfileSubscription &&
          // !this.subscriptionExists("TeacherProfile") &&
          !router.currentRoute.path.includes("/admin") &&
          newVal === false &&
          oldVal === true
        ) {
          // console.log('this.syncedTeacherProfileModel: ', this.syncedTeacherProfileModel);
          // console.log("changingDatastoreConfig syncTeacherProfile");
          this.syncTeacherProfile();

          // if (this.syncedTeacherProfile) {
          //   console.log("app.vue watch changingDatastoreConfig syncData");
          //   this.syncData();
          // }
        }
      },
      // deep: true,
    },
  },

  methods: {
    goToJustBoughtProduct() {
      console.log("goToJustBoughtProduct clicked");
      if (this.justBoughtProduct) {
        const productId = this.justBoughtProduct.id;
        if (router.currentRoute.path.includes("/app")) {
          router.push(`/app/products/${productId}`).catch(() =>
            // error
            {
              // console.log(error);
            },
          );
        } else {
          router.push(`/just-bought-product/${productId}`).catch(() =>
            // error
            {
              // console.log(error);
            },
          );
        }

        this.justBoughtSnackbar = false;
      }
    },

    async showJustBoughtSnackbar(item) {
      const model = item.Product || item.ProductBundle;
      if (!model) return;

      this.justBoughtProduct = model;

      if (model.thumbnails?.length > 0) {
        const key = model.thumbnails[0];
        await Storage.get(key, { level: "public" })
          .then((url) => {
            this.justBoughtThumbnailUrl = url;
            this.justBoughtSnackbar = true;
          })
          .catch(() => {
            this.justBoughtThumbnailUrl = "";
            this.justBoughtSnackbar = true;
          });
      } else {
        this.justBoughtThumbnailUrl = "";
        this.justBoughtSnackbar = true;
      }
    },

    parseGraphqlResult(data) {
      if (data === null || data === undefined) return data;

      // If it's an array, map over it recursively
      if (Array.isArray(data)) {
        return data.map(this.parseGraphqlResult);
      }

      // If it's an object, iterate keys
      if (typeof data === "object") {
        const parsedObject = {};
        for (const [key, value] of Object.entries(data)) {
          parsedObject[key] = this.parseGraphqlResult(value);
        }
        return parsedObject;
      }

      // If it's a string, try to parse it
      if (typeof data === "string") {
        try {
          // Basic check to avoid parsing simple strings like "Hello"
          // Only parse if it starts with { or [
          const trimmed = data.trim();
          if (
            (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
            (trimmed.startsWith("[") && trimmed.endsWith("]"))
          ) {
            return JSON.parse(data);
          }
        } catch (e) {
          // If parsing fails, just return the original string
          return data;
        }
      }

      return data;
    },

    async createTeacherProfile() {
      try {
        const user = await Auth.currentAuthenticatedUser().catch(() => {
          // eslint-disable-next-line @typescript-eslint/no-empty-function
        });

        if (user) {
          console.log("app.vue createTeacherProfile");
          this.createdNewTeacherProfile = true;

          await API.graphql({
            query: createTeacherProfile,
            variables: {
              input: {
                title: "",
                name: "",
                surname: "",
                language: "",
                province: "",
                daysPerWeek: 0,
                status: "Active",
                type: "TeacherProfile",
              },
            },
            authMode: this.authMode,
          });

          // await DataStore.save(
          //   new TeacherProfile({
          //     title: "",
          //     name: "",
          //     surname: "",
          //     language: "",
          //     province: "",
          //     daysPerWeek: 0,
          //     status: "Active",
          //     TeacherSchedules: [],
          //     TeacherClassSubjects: [],
          //     type: "TeacherProfile",
          //   }),
          // );

          // const newProfile = await API.graphql({
          //   query: createTeacherProfile,
          //   variables: {
          //     input: {
          //       title: "",
          //       name: "",
          //       surname: "",
          //       language: "",
          //       province: "",
          //       daysPerWeek: 0,
          //       status: "Active",
          //       TeacherSchedules: [],
          //       TeacherClassSubjects: [],
          //       type: "TeacherProfile",
          //     },
          //   },
          //   authMode: this.authMode,
          // });

          // const parsedItem = this.parseGraphqlResult(
          //   newProfile.data.createTeacherProfile,
          // );
          // this.handleTeacherProfileUpdate(parsedItem);
        }
      } catch (error) {
        console.log(error);
      }
    },

    // async getTeacherProfile(user) {
    //   try {
    //     console.log("user:", user);
    //     const result =
    //       // await API.graphql({
    //       //   query: teacherProfilesByOwner,
    //       //   variables: { owner: user.username, filter: { _deleted: { ne: true } } },
    //       // });
    //       await API.graphql(graphqlOperation(listTeacherProfiles));

    //     console.log("result:", result);
    //     return result.data.getTeacherProfile;
    //   } catch (error) {
    //     console.log(error);
    //   }
    // },

    async checkTeacherRole() {
      if (!this.userGroups?.includes("teacher")) {
        console.log("User is not a teacher");
        // const cognitoUsername = this.syncedTeacherProfileModel.owner
        //  .split("::")
        //  .pop();

        const user = await Auth.currentAuthenticatedUser().catch(() => {
          // eslint-disable-next-line @typescript-eslint/no-empty-function
        });

        const cognitoUsername = user.username;

        // console.log("cognitoUsername:", cognitoUsername);

        API.post("cognitolambdaapi", "/item/addUserToGroup", {
          body: {
            groupName: "teacher",
            userName: cognitoUsername,
          },
        })
          .then(() => {
            let newUserGroups = this.userGroups ? [...this.userGroups] : [];
            newUserGroups.push("teacher");
            // console.log("this.localUserGroups: ", this.localUserGroups);
            store.commit("storeUserGroups", newUserGroups);
            console.log("User added to the teacher group");
          })
          .catch((error) => {
            // Log any errors
            console.log("error:", error);
          });
      }
      //  else {
      //   console.log("User is a teacher");
      // }
    },

    // async changeDataStoreConfig(payload, resolve, reject) {
    //   try {
    //     store.commit("changingDatastoreConfig", true);

    //     console.log("app.vue changeDataStoreConfig");
    //     console.log("Event received in App.vue:", payload);
    //     // if (this.teacherProfileSubscription) {
    //     //   this.teacherProfileSubscription.unsubscribe();
    //     //   this.teacherProfileSubscription = null;
    //     // }

    //     // if (this.announcementSubscription) {
    //     //   this.announcementSubscription.unsubscribe();
    //     //   this.announcementSubscription = null;
    //     // }

    //     // if (this.teacherProductsOrderSubscription) {
    //     //   this.teacherProductsOrderSubscription.unsubscribe();
    //     //   this.teacherProductsOrderSubscription = null;
    //     // }
    //     // console.log("changeDataStoreConfig unsubscribe");

    //     await DataStore.stop().then(() => {
    //       console.log("DataStore stopped");
    //     });

    //     await DataStore.start();
    //     console.log("DataStore started");

    //     const user = await Auth.currentAuthenticatedUser().catch(() => {
    //       // eslint-disable-next-line @typescript-eslint/no-empty-function
    //     });

    //     if (user) {
    //       console.log("changeDataStoreConfig syncTeacherProfile");
    //       this.syncTeacherProfile();
    //     }

    //     // this.changedDatastorePredicate = true;

    //     return true;
    //   } catch (error) {
    //     console.log("changeDataStoreConfig error:", error);
    //     return error;
    //   }
    //    finally {
    //     console.log("changeDataStoreConfig finally");
    //     store.commit("changingDatastoreConfig", false);
    //   }
    // },

    createEmailTemplate() {
      AWS.config.update({
        region: "af-south-1",
        accessKeyId: "",
        secretAccessKey: "",
      });
      var ses = new AWS.SES();

      //       var params = {
      //         Template: {
      //           TemplateName: "ProductsOrderPaidEFTTemplate",
      //           SubjectPart:
      //             "Juffrou Anri Se Klaskamer: Your files are ready for download",
      //           TextPart: `Your Order is Ready for Download

      // Hello {{customer_name}},

      // Your order {{order_number}} on {{order_date}} has been paid. The following products are available for download:

      // {{product_links_text}}

      // {{#if shippingDetails}}
      // Shipping Details:
      // Address: {{shippingDetails.address1}}
      // {{#if shippingDetails.address2 }}
      // Address 2: {{shippingDetails.address2}}
      // {{/if}}
      // City: {{shippingDetails.city}}
      // Province: {{shippingDetails.province}}
      // Postal Code: {{shippingDetails.postalCode}}
      // Country: {{shippingDetails.country}}
      // Phone Number: {{shippingDetails.phoneNumber}}
      // {{/if}}

      // Thanks for shopping with us!

      // You can check the status of your orders at any time at {{base_url}}/app/productorders.

      // Have questions? We’re here for you. Contact us at {{contact_us_email}} or call {{contact_us_phone}}.

      // Sincerely,
      // Juffrou Anri Se Klaskamer`,
      //           HtmlPart: `<!DOCTYPE html>
      // <html lang="en">
      // <head>
      //     <meta charset="UTF-8">
      //     <meta name="viewport" content="width=device-width, initial-scale=1.0">
      //     <title>Your Order is Ready for Download</title>
      //     <style>
      //         body {
      //             font-family: Arial, sans-serif;
      //             line-height: 1.6;
      //             margin: 0;
      //             padding: 20px;
      //             background-color: #f9f9f9;
      //         }
      //         .container {
      //             max-width: 600px;
      //             margin: auto;
      //             background: #fff;
      //             padding: 20px;
      //             border-radius: 8px;
      //             box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      //         }
      //         h1 {
      //             color: #333;
      //         }
      //         h2 {
      //             color: #333;
      //         }
      //         p {
      //             color: #555;
      //         }
      //         a {
      //             color: #007BFF;
      //             text-decoration: none;
      //         }
      //         .footer {
      //             margin-top: 20px;
      //             font-size: 0.9em;
      //             color: #777;
      //         }
      //         .product-link {
      //             margin: 5px 0;
      //         }
      //         .shipping-info {
      //             margin-top: 20px;
      //             padding: 10px;
      //             border: 1px solid #ddd;
      //             border-radius: 5px;
      //             background-color: #f7f7f7;
      //         }
      //     </style>
      // </head>
      // <body>
      //     <div class="container">
      //         <h1>Files Ready for Download</h1>
      //         <p>Hello {{customer_name}},</p>
      //         <p>Your order <strong>{{order_number}}</strong> on <strong>{{order_date}}</strong> has been paid. The following products are available for download:</p>
      //         <div>
      //             {{product_links_html}}
      //         </div>

      // {{#if shippingDetails}}
      //         <div class="shipping-info">
      //             <h2>Shipping Details:</h2>
      //             <p>Address: {{shippingDetails.address1}}<br>
      // {{#if shippingDetails.address2}}
      //             Address 2: {{shippingDetails.address2}}<br>
      // {{/if}}
      //             City: {{shippingDetails.city}}<br>
      //         Province: {{shippingDetails.province}}<br>
      //         Postal Code: {{shippingDetails.postalCode}}<br>
      //         Country: {{shippingDetails.country}}<br>
      //         Phone Number: {{shippingDetails.phoneNumber}}</p>
      //         </div>
      // {{/if}}

      //         <p>Thanks for shopping with us!</p>
      //         <p>You can check the status of your orders at any time in <a href="{{base_url}}/app/productorders">Orders history</a>.</p>
      //         <p>Have questions? We’re here for you. Contact us at <a href="mailto:{{contact_us_email}}">{{contact_us_email}}</a> or call <a href="tel:{{contact_us_phone}}">{{contact_us_phone}}</a>.</p>
      //         <div class="footer">
      //             <p>Sincerely,<br><a href="{{base_url}}">Juffrou Anri Se Klaskamer</a></p>
      //             <p><a href="{{facebook_link}}">Facebook</a></p>
      //         </div>
      //     </div>
      // </body>
      // </html>`,
      //         },
      //       };
      // var params = {
      //   Template: {
      //     TemplateName: "ProductsOrderProcessingEFTCouponTemplate-v1_0",
      //     SubjectPart:
      //       "Order {{order_number}} Confirmation — Juffrou Anri Se Klaskamer",
      //     TextPart: `Order Confirmation

      //     Hello {{customer_name}},

      //     Thank you for your order with Juffrou Anri Se Klaskamer! We have received your order dated {{order_date}} and will process it shortly.
      //     Order {{order_number}}
      //     {{#if coupon}}
      //     Coupon Code: {{coupon.couponCode}}
      //     {{/if}}
      //     Payment status: {{payment_status}}

      //     Bank details are as follows:
      //     {{bank_details_text}}

      //     PLEASE REMEMBER THAT PAYMENTS TAKE LONGER OVER THE WEEKEND TO REFLECT. PLEASE SEND IMMEDIATE PAYMENT OR PAY WITH A CREDIT CARD TO AVOID DISAPPOINTMENT!

      //     All orders will be sent via email once payment reflects. Refer to the email "files ready for download" and click on the download links.

      //     View your order here: {{order_link}}

      //     {{#if shippingDetails}}
      //     Shipping Details:
      //     Address: {{shippingDetails.address1}}
      //     {{#if shippingDetails.address2 }}
      //     Address 2: {{shippingDetails.address2}}
      //     {{/if}}
      //     City: {{shippingDetails.city}}
      //     Province: {{shippingDetails.province}}
      //     Postal Code: {{shippingDetails.postalCode}}
      //     Country: {{shippingDetails.country}}
      //     Phone Number: {{shippingDetails.phoneNumber}}
      //     {{/if}}

      //     Thanks for shopping with us!

      //     You can check the status of your orders at any time at {{base_url}}/app/productorders.

      //     We welcome you to our store anytime. If you need assistance or have any questions, please email us at {{contact_us_email}} or call {{contact_us_phone}}. We are happy to help!

      //     Sincerely,
      //     Juffrou Anri Se Klaskamer`,
      //     HtmlPart: `<!DOCTYPE html>
      //     <html lang="en">
      //     <head>
      //         <meta charset="UTF-8">
      //         <meta name="viewport" content="width=device-width, initial-scale=1.0">
      //         <title>Order Confirmation</title>
      //         <style>
      //             body {
      //                 font-family: Arial, sans-serif;
      //                 line-height: 1.6;
      //                 margin: 0;
      //                 padding: 20px;
      //                 background-color: #f9f9f9;
      //             }
      //             .container {
      //                 max-width: 600px;
      //                 margin: auto;
      //                 background: #fff;
      //                 padding: 20px;
      //                 border-radius: 8px;
      //                 box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      //             }
      //             h1 {
      //                 color: #333;
      //             }
      //             h2 {
      //                 color: #333;
      //             }
      //             p, strong {
      //                 color: #555;
      //             }
      //             a {
      //                 color: #007BFF;
      //                 text-decoration: none;
      //             }
      //             .footer {
      //                 margin-top: 20px;
      //                 font-size: 0.9em;
      //                 color: #777;
      //             }
      //             .bank-details {
      //                 margin: 10px 0;
      //                 font-size: 0.9em;
      //             }
      //             .shipping-info {
      //                 margin-top: 20px;
      //                 padding: 10px;
      //                 border: 1px solid #ddd;
      //                 border-radius: 5px;
      //                 background-color: #f7f7f7;
      //             }
      //         </style>
      //     </head>
      //     <body>
      //         <div class="container">
      //             <h1>Order Confirmation</h1>
      //             <p>Hello {{customer_name}},</p>
      //             <p>Thank you for your order with Juffrou Anri Se Klaskamer! We have received your order dated {{order_date}} and will process it shortly.</p>
      //             <p><strong>Order {{order_number}}</strong></p>
      //             {{#if coupon}}
      //             <p><strong>Coupon Code:</strong> {{coupon.couponCode}}</p>
      //             {{/if}}
      //             <p><strong>Payment status:</strong> {{payment_status}}</p>

      //             <div class="bank-details">
      //                 <strong>Bank details are as follows:</strong><br>
      //                 {{bank_details_html}}
      //             </div>

      //             <p><strong>IMPORTANT:</strong> PLEASE REMEMBER THAT PAYMENTS TAKE LONGER OVER THE WEEKEND TO REFLECT. PLEASE SEND IMMEDIATE PAYMENT OR PAY WITH A CREDIT CARD TO AVOID DISAPPOINTMENT!</p>

      //             <p>All orders will be sent via email once payment reflects. Refer to the email "files ready for download" and click on the download links.</p>

      //             <h3><a href="{{order_link}}">View your order here</a></h3>

      //             {{#if shippingDetails}}
      //                     <div class="shipping-info">
      //                         <h2>Shipping Details:</h2>
      //                         <p>Address: {{shippingDetails.address1}}<br>
      //             {{#if shippingDetails.address2}}
      //                         Address 2: {{shippingDetails.address2}}<br>
      //             {{/if}}
      //                     City: {{shippingDetails.city}}<br>
      //                     Province: {{shippingDetails.province}}<br>
      //                     Postal Code: {{shippingDetails.postalCode}}<br>
      //                     Country: {{shippingDetails.country}}<br>
      //                     Phone Number: {{shippingDetails.phoneNumber}}</p>
      //                     </div>
      //             {{/if}}

      //             <p>Thanks for shopping with us!</p>
      //             <p>You can check the status of your orders at any time on our <a href="{{base_url}}/app/productorders">Orders History Page</a>.</p>

      //             <p>We welcome you to our store anytime. If you need assistance or have any questions, please email us at <a href="mailto:{{contact_us_email}}">{{contact_us_email}}</a> or call <a href="tel:{{contact_us_phone}}">{{contact_us_phone}}</a>. We are happy to help!</p>

      //             <div class="footer">
      //                 <p>Sincerely,<br><a href="{{base_url}}">Juffrou Anri Se Klaskamer</a></p>
      //                 <p><a href="{{facebook_link}}">Facebook</a></p>
      //             </div>
      //         </div>
      //     </body>
      //     </html>`,
      //   },
      // };

      //       For security reasons, this temporary password is valid for a limited time. If you did not request this password reset, please contact us immediately at {{contact_us_email}}.

      // If you need further assistance, feel free to reach out to us. We are here to help!

      /* <p><strong>Important:</strong> This temporary password is only valid for a limited time. If you did not request this reset, please contact us immediately at <a href="mailto:{{contact_us_email}}">{{contact_us_email}}</a>.</p>

    <p>If you need further assistance, feel free to reach out to us at <a href="mailto:{{contact_us_email}}">{{contact_us_email}}</a> or call <a href="tel:{{contact_us_phone}}">{{contact_us_phone}}</a>.</p> */

      var params = {
        Template: {
          TemplateName: "ProductsOrderQuotationTemplate-v1_2",
          SubjectPart: "Quotation {{order_number}} — Juffrou Anri Se Klaskamer",
          TextPart: `Quotation

      {{#if quoteNote}}
      {{quoteNote}}
      {{/if}}

      Hello {{customer_name}},

      Thank you for requesting a quote from Juffrou Anri Se Klaskamer! Below is your quotation dated {{order_date}}:

      Quotation {{order_number}}

      {{#if couponCode}}
      Coupon Code: {{couponCode}}
      {{/if}}

      Products:
      {{#each products}}
      - {{this.title}}: {{this.price}}
      {{/each}}

      {{#if shipping_amount}}
      Shipping: {{shipping_amount}}
      {{/if}}
      {{#if couponValueText}}
      Coupon Discount: {{couponValueText}}
      {{/if}}

      Subtotal (incl. VAT): {{subtotal_incl_vat}}
      Total (excl. VAT): {{total_excl_vat}}
      VAT (15%): {{vat_amount}}
      Total: {{total}}

      To proceed with this quotation, continue this order at {{base_url}}/app/products.

      Bank details are as follows:
      {{bank_details_text}}

      {{#if shippingDetails}}
      Shipping Details:
      Address: {{shippingDetails.address1}}
      {{#if shippingDetails.address2 }}
      Address 2: {{shippingDetails.address2}}
      {{/if}}
      City: {{shippingDetails.city}}
      Province: {{shippingDetails.province}}
      Postal Code: {{shippingDetails.postalCode}}
      Country: {{shippingDetails.country}}
      Phone Number: {{shippingDetails.phoneNumber}}
      {{/if}}

      We welcome you to our store anytime. If you need assistance or have any questions, please email us at {{contact_us_email}} or call {{contact_us_phone}}. We are happy to help!

      Sincerely,
      Juffrou Anri Se Klaskamer`,
          HtmlPart: `<!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Quotation</title>
          <style>
              body {
                  font-family: Arial, sans-serif;
                  line-height: 1.6;
                  margin: 0;
                  padding: 20px;
                  background-color: #f9f9f9;
              }
              .container {
                  max-width: 600px;
                  margin: auto;
                  background: #fff;
                  padding: 20px;
                  border-radius: 8px;
                  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              }
              h1 {
                  color: #333;
              }
              h2 {
                  color: #333;
              }
              p, strong {
                  color: #555;
              }
              a {
                  color: #007BFF;
                  text-decoration: none;
              }
              .footer {
                  margin-top: 20px;
                  font-size: 0.9em;
                  color: #777;
              }
              .quote-table {
                  width: 100%;
                  border-collapse: collapse;
                  margin: 20px 0;
              }
              .quote-table th, .quote-table td {
                  padding: 10px;
                  text-align: left;
                  border-bottom: 1px solid #ddd;
              }
              .quote-table th {
                  background-color: #f7f7f7;
              }
              .quote-table .right-align {
                  text-align: right;
              }
              .quote-table .total-row {
                  font-weight: bold;
              }
              .shipping-info {
                  margin-top: 20px;
                  padding: 10px;
                  border: 1px solid #ddd;
                  border-radius: 5px;
                  background-color: #f7f7f7;
              }
          </style>
      </head>
      <body>
          <div class="container">
              <h1>Quotation</h1>
              {{#if quoteNoteHTML}}
              {{quoteNoteHTML}}
              {{/if}}
              <p>Hello {{customer_name}},</p>
              <p>Thank you for requesting a quote from Juffrou Anri Se Klaskamer! Below is your quotation dated {{order_date}}:</p>
              <p><strong>Quotation {{order_number}}</strong></p>
              {{#if couponCode}}
              <p><strong>Coupon Code:</strong> {{couponCode}}</p>
              {{/if}}

              <table class="quote-table">
                  <thead>
                      <tr>
                          <th>Product</th>
                          <th class="right-align">Price</th>
                      </tr>
                  </thead>
                  <tbody>
                      {{#each products}}
                      <tr>
                          <td>{{this.title}}</td>
                          <td class="right-align">{{this.price}}</td>
                      </tr>
                      {{/each}}
                      {{#if shipping_amount}}
                      <tr>
                          <td>Shipping</td>
                          <td class="right-align">{{shipping_amount}}</td>
                      </tr>
                      {{/if}}
                      {{#if couponValueText}}
                      <tr>
                          <td>Coupon Discount</td>
                          <td class="right-align">{{couponValueText}}</td>
                      </tr>
                      {{/if}}
                      <tr>
                          <td>Subtotal (incl. VAT)</td>
                          <td class="right-align">{{subtotal_incl_vat}}</td>
                      </tr>
                      <tr>
                          <td>Total (excl. VAT)</td>
                          <td class="right-align">{{total_excl_vat}}</td>
                      </tr>
                      <tr>
                          <td>VAT (15%)</td>
                          <td class="right-align">{{vat_amount}}</td>
                      </tr>
                      <tr class="total-row">
                          <td>Total</td>
                          <td class="right-align">{{total}}</td>
                      </tr>
                  </tbody>
              </table>

              <p>To proceed with this quotation, continue this order at <a href="{{base_url}}/app/products">our store</a>.</p>
              <div class="bank-details">
                  <strong>Bank details are as follows:</strong><br>
                  {{bank_details_html}}
              </div>

              {{#if shippingDetails}}
              <div class="shipping-info">
                  <h2>Shipping Details:</h2>
                  <p>Address: {{shippingDetails.address1}}<br>
                  {{#if shippingDetails.address2}}
                  Address 2: {{shippingDetails.address2}}<br>
                  {{/if}}
                  City: {{shippingDetails.city}}<br>
                  Province: {{shippingDetails.province}}<br>
                  Postal Code: {{shippingDetails.postalCode}}<br>
                  Country: {{shippingDetails.country}}<br>
                  Phone Number: {{shippingDetails.phoneNumber}}</p>
              </div>
              {{/if}}

              <p>We welcome you to our store anytime. If you need assistance or have any questions, please email us at <a href="mailto:{{contact_us_email}}">{{contact_us_email}}</a> or call <a href="tel:{{contact_us_phone}}">{{contact_us_phone}}</a>. We are happy to help!</p>

              <div class="footer">
                  <p>Sincerely,<br><a href="{{base_url}}">Juffrou Anri Se Klaskamer</a></p>
                  <p><a href="{{facebook_link}}">Facebook</a></p>
              </div>
          </div>
      </body>
      </html>`,
        },
      };

      //       var params = {
      //         Template: {
      //           TemplateName: "TemporaryPasswordEmailTemplate",
      //           SubjectPart: "Your Temporary Password for {{app_name}}",
      //           TextPart: `Hello {{user_name}},

      // We have received your request to retrieve your password for {{app_name}}.

      // Here is your temporary password: {{temporary_password}}

      // Alternatively you can use the temporary password URL to login: {{temporary_password_url}}

      // Please use this password to log in to your account. Once logged in, you will be able to change your password to something more secure.

      // We welcome you to our store anytime. If you need assistance or have any questions, please email us at {{contact_us_email}} or call {{contact_us_phone}}. We are happy to help!

      // Sincerely,
      // Juffrou Anri Se Klaskamer`,

      //           HtmlPart: `<!DOCTYPE html>
      // <html lang="en">
      // <head>
      //     <meta charset="UTF-8">
      //     <meta name="viewport" content="width=device-width, initial-scale=1.0">
      //     <title>Your Temporary Password</title>
      //     <style>
      //         body {
      //             font-family: Arial, sans-serif;
      //             line-height: 1.6;
      //             margin: 0;
      //             padding: 20px;
      //             background-color: #f9f9f9;
      //         }
      //         .container {
      //             max-width: 600px;
      //             margin: auto;
      //             background: #fff;
      //             padding: 20px;
      //             border-radius: 8px;
      //             box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      //         }
      //         h1 {
      //             color: #333;
      //         }
      //         p, strong {
      //             color: #555;
      //         }
      //         a {
      //             color: #007BFF;
      //             text-decoration: none;
      //         }
      //         .footer {
      //             margin-top: 20px;
      //             font-size: 0.9em;
      //             color: #777;
      //         }
      //     </style>
      // </head>
      // <body>
      //     <div class="container">
      //         <h1>Your Temporary Password</h1>
      //         <p>Hello {{user_name}},</p>

      //         <p>We have received your request to retrieve your password for {{app_name}}. Below is your temporary password:</p>

      //         <p><strong>Temporary Password:</strong> {{temporary_password}}</p>

      //         <p>Alternatively you can use the <strong>Temporary Password URL</strong> to login: <a href="{{temporary_password_url}}">{{temporary_password_url}}</a></p>

      //         <p>Please use this password to log in to your account. Once logged in, you will be able to change your password to something more secure.</p>

      //         <p>We welcome you to our store anytime. If you need assistance or have any questions, please email us at <a href="mailto:{{contact_us_email}}">{{contact_us_email}}</a> or call <a href="tel:{{contact_us_phone}}">{{contact_us_phone}}</a>. We are happy to help!</p>

      //         <div class="footer">
      //             <p>Sincerely,<br><a href="{{base_url}}">Juffrou Anri Se Klaskamer</a></p>
      //             <p><a href="{{facebook_link}}">Facebook</a></p>
      //         </div>
      //     </div>
      // </body>
      // </html>`,
      //         },
      //       };

      //   var params = {
      //     Template: {
      //       TemplateName: "ProductsOrderInvoiceTemplate-v1_1",
      //       SubjectPart: "Invoice {{order_number}} — Juffrou Anri Se Klaskamer",
      //       TextPart: `Invoice

      //   Hello {{customer_name}},

      //   Thank you for your purchase from Juffrou Anri Se Klaskamer! Below is your invoice dated {{order_date}} for order {{order_number}}:

      //   Invoice {{order_number}}
      //   Issue Date: {{order_date}}

      //   {{#if couponCode}}
      //   Coupon Code: {{couponCode}}
      //   {{/if}}

      //   Products:
      //   {{#each products}}
      //   - {{this.title}}: {{this.price}}
      //     {{#each this.addons}}
      //     -   ↳ {{this.title}} {{#if this.type}}({{this.type}}){{/if}}: R{{this.price}}
      //     {{/each}}
      //   {{/each}}

      //   {{#if shipping_amount}}
      //   Shipping: {{shipping_amount}}
      //   {{/if}}
      //   {{#if couponValueText}}
      //   Coupon Discount: {{couponValueText}}
      //   {{/if}}

      //   Subtotal (incl. VAT): {{subtotal_incl_vat}}
      //   Total (excl. VAT): {{total_excl_vat}}
      //   VAT (15%): {{vat_amount}}
      //   Total: {{total}}

      //   Payment Details:
      //   Payment Status: {{payment_status}}
      //   Payment Method: {{payment_method}}

      //   {{#if bank_details_text}}
      //   Bank details:
      //   {{bank_details_text}}
      //   {{/if}}

      //   {{#if shippingDetails}}
      //   Shipping Details:
      //   Address: {{shippingDetails.address1}}
      //   {{#if shippingDetails.address2}}
      //   Address 2: {{shippingDetails.address2}}
      //   {{/if}}
      //   City: {{shippingDetails.city}}
      //   Province: {{shippingDetails.province}}
      //   Postal Code: {{shippingDetails.postalCode}}
      //   Country: {{shippingDetails.country}}
      //   Phone Number: {{shippingDetails.phoneNumber}}
      //   {{/if}}

      //   For any questions regarding this invoice, please contact us at {{contact_us_email}} or call {{contact_us_phone}}. Thank you for your business!

      //   Sincerely,
      //   Juffrou Anri Se Klaskamer`,
      //       HtmlPart: `<!DOCTYPE html>
      //   <html lang="en">
      //   <head>
      //       <meta charset="UTF-8">
      //       <meta name="viewport" content="width=device-width, initial-scale=1.0">
      //       <title>Invoice</title>
      //       <style>
      //     body {
      //         font-family: Arial, sans-serif;
      //         line-height: 1.6;
      //         margin: 0;
      //         padding: 20px;
      //         background-color: #f9f9f9;
      //     }
      //     .container {
      //         max-width: 600px;
      //         margin: auto;
      //         background: #fff;
      //         padding: 20px;
      //         border-radius: 8px;
      //         box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      //     }
      //     h1 {
      //         color: #333;
      //     }
      //     h2 {
      //         color: #333;
      //     }
      //     p, strong {
      //         color: #555;
      //     }
      //     a {
      //         color: #007BFF;
      //         text-decoration: none;
      //     }
      //     .footer {
      //         margin-top: 20px;
      //         font-size: 0.9em;
      //         color: #777;
      //     }
      //     .invoice-table {
      //         width: 100%;
      //         border-collapse: collapse;
      //         margin: 20px 0;
      //         table-layout: fixed; /* Enforce column widths */
      //     }
      //     .invoice-table th, .invoice-table td {
      //         padding: 10px;
      //         text-align: left;
      //         border-bottom: 1px solid #ddd;
      //     }
      //     .invoice-table th {
      //         background-color: #f7f7f7;
      //     }
      //     .invoice-table .right-align {
      //         text-align: right;
      //         min-width: 120px; /* Ensure price column has enough space */
      //     }
      //     .invoice-table .product-column {
      //         word-wrap: break-word; /* Allow product names to wrap */
      //         width: auto; /* Let product column take remaining space */
      //     }
      //     .invoice-table .total-row {
      //         font-weight: bold;
      //     }
      //     .shipping-info, .payment-info, .bank-details {
      //         margin-top: 20px;
      //         padding: 10px;
      //         border: 1px solid #ddd;
      //         border-radius: 5px;
      //         background-color: #f7f7f7;
      //     }
      //     .order-status {
      //         color: #28a745;
      //         font-weight: bold;
      //     }
      // </style>
      //   </head>
      //   <body>
      //       <div class="container">
      //           <h1>Invoice</h1>
      //           <p>Hello {{customer_name}},</p>
      //           <p>Thank you for your purchase from Juffrou Anri Se Klaskamer! Below is your invoice dated {{order_date}} for order {{order_number}}:</p>
      //           <p><strong>Invoice {{order_number}}</strong><br>
      //           Issue Date: {{order_date}}<br>

      //           {{#if couponCode}}
      //           <p><strong>Coupon Code:</strong> {{couponCode}}</p>
      //           {{/if}}

      //           <table class="invoice-table">
      //         <thead>
      //             <tr>
      //                 <th class="product-column">Product</th>
      //                 <th class="right-align">Price</th>
      //             </tr>
      //         </thead>
      //         <tbody>
      //             {{#each products}}
      //             <tr>
      //                 <td class="product-column">{{this.title}}</td>
      //                 <td class="right-align">{{this.price}}</td>
      //             </tr>

      //             {{#each this.addons}}
      //               <tr style="background-color: #f9f9f9;">
      //                 <td class="product-column" style="padding-left: 30px; font-size: 0.95em; color: #555;">
      //                   ↳ {{this.title}} {{#if this.type}}({{this.type}}){{/if}}
      //                 </td>
      //                 <td class="right-align" style="font-size: 0.95em; color: #555;">
      //                   R{{this.price}}
      //                 </td>
      //               </tr>
      //             {{/each}}
      //             {{/each}}

      //             {{#if shipping_amount}}
      //             <tr>
      //                 <td class="product-column">Shipping</td>
      //                 <td class="right-align">{{shipping_amount}}</td>
      //             </tr>
      //             {{/if}}
      //             {{#if couponValueText}}
      //             <tr>
      //                 <td class="product-column">Coupon Discount</td>
      //                 <td class="right-align">{{couponValueText}}</td>
      //             </tr>
      //             {{/if}}
      //             <tr>
      //                 <td class="product-column">Subtotal (incl. VAT)</td>
      //                 <td class="right-align">{{subtotal_incl_vat}}</td>
      //             </tr>
      //             <tr>
      //                 <td class="product-column">Total (excl. VAT)</td>
      //                 <td class="right-align">{{total_excl_vat}}</td>
      //             </tr>
      //             <tr>
      //                 <td class="product-column">VAT (15%)</td>
      //                 <td class="right-align">{{vat_amount}}</td>
      //             </tr>
      //             <tr class="total-row">
      //                 <td class="product-column">Total</td>
      //                 <td class="right-align">{{total}}</td>
      //             </tr>
      //         </tbody>
      //     </table>

      //           <div class="payment-info">
      //               <h2>Payment Details:</h2>
      //               <p>Payment Status: <span class="order-status">{{payment_status}}</span><br>
      //               Payment Method: {{payment_method}}</p>
      //           </div>

      //           {{#if bank_details_html}}
      //           <div class="bank-details">
      //               <h2>Bank details:</h2>
      //               <p>{{bank_details_html}}</p>
      //           </div>
      //           {{/if}}

      //           {{#if shippingDetails}}
      //           <div class="shipping-info">
      //               <h2>Shipping Details:</h2>
      //               <p>Address: {{shippingDetails.address1}}<br>
      //               {{#if shippingDetails.address2}}
      //               Address 2: {{shippingDetails.address2}}<br>
      //               {{/if}}
      //               City: {{shippingDetails.city}}<br>
      //               Province: {{shippingDetails.province}}<br>
      //               Postal Code: {{shippingDetails.postalCode}}<br>
      //               Country: {{shippingDetails.country}}<br>
      //               Phone Number: {{shippingDetails.phoneNumber}}</p>
      //           </div>
      //           {{/if}}

      //           <p>For any questions regarding this invoice, please email us at <a href="mailto:{{contact_us_email}}">{{contact_us_email}}</a> or call <a href="tel:{{contact_us_phone}}">{{contact_us_phone}}</a>. Thank you for your business!</p>

      //           <div class="footer">
      //               <p>Sincerely,<br><a href="{{base_url}}">Juffrou Anri Se Klaskamer</a></p>
      //               <p><a href="{{facebook_link}}">Facebook</a></p>
      //           </div>
      //       </div>
      //   </body>
      //   </html>`,
      //     },
      //   };

      //       var params = {
      //         Template: {
      //           TemplateName: "RetrieveUsernameEmailTemplate",
      //           SubjectPart: "Your Username for {{app_name}}",
      //           TextPart: `Hello {{user_name}},

      // We have received your request to retrieve your username for {{app_name}}.

      // Here is your username: {{username}}

      // Please use this username to log in to your account.

      // We welcome you to our store anytime. If you need assistance or have any questions, please email us at {{contact_us_email}} or call {{contact_us_phone}}. We are happy to help!

      // Sincerely,
      // Juffrou Anri Se Klaskamer`,

      //           HtmlPart: `<!DOCTYPE html>
      // <html lang="en">
      // <head>
      //     <meta charset="UTF-8">
      //     <meta name="viewport" content="width=device-width, initial-scale=1.0">
      //     <title>Your Username</title>
      //     <style>
      //         body {
      //             font-family: Arial, sans-serif;
      //             line-height: 1.6;
      //             margin: 0;
      //             padding: 20px;
      //             background-color: #f9f9f9;
      //         }
      //         .container {
      //             max-width: 600px;
      //             margin: auto;
      //             background: #fff;
      //             padding: 20px;
      //             border-radius: 8px;
      //             box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      //         }
      //         h1 {
      //             color: #333;
      //         }
      //         p, strong {
      //             color: #555;
      //         }
      //         a {
      //             color: #007BFF;
      //             text-decoration: none;
      //         }
      //         .footer {
      //             margin-top: 20px;
      //             font-size: 0.9em;
      //             color: #777;
      //         }
      //     </style>
      // </head>
      // <body>
      //     <div class="container">
      //         <h1>Your Username</h1>
      //         <p>Hello {{user_name}},</p>

      //         <p>We have received your request to retrieve your username for {{app_name}}. Below is your username:</p>

      //         <p><strong>Username:</strong> {{username}}</p>

      //         <p>Please use this username to log in to your account.</p>

      //         <p>We welcome you to our store anytime. If you need assistance or have any questions, please email us at <a href="mailto:{{contact_us_email}}">{{contact_us_email}}</a> or call <a href="tel:{{contact_us_phone}}">{{contact_us_phone}}</a>. We are happy to help!</p>

      //         <div class="footer">
      //             <p>Sincerely,<br><a href="{{base_url}}">Juffrou Anri Se Klaskamer</a></p>
      //             <p><a href="{{facebook_link}}">Facebook</a></p>
      //         </div>
      //     </div>
      // </body>
      // </html>`,
      //         },
      //       };

      //       var params = {
      //         Template: {
      //           TemplateName: "AbandonedCartDiscountTemplate-v1_1",
      //           SubjectPart:
      //             "Special 10% Discount Offer on Your Abandoned Cart — Juffrou Anri Se Klaskamer",
      //           TextPart: `Special Discount Offer
      // Hello {{customer_name}},

      // We noticed you left some items in your cart on {{order_date}}. To help you complete your purchase, we've created a special 10% discount coupon just for you!

      // Coupon Code: {{coupon_code}}
      // Valid Until: {{coupon_end_date}}

      // To apply this discount and complete your order, visit {{base_url}}/app/products.

      // We welcome you to our store anytime. If you need assistance or have any questions, please email us at {{contact_us_email}} or call {{contact_us_phone}}. We are happy to help!

      // Sincerely,
      // Juffrou Anri Se Klaskamer`,
      //           HtmlPart: `<!DOCTYPE html>
      // <html lang="en">
      // <head>
      // <meta charset="UTF-8">
      // <meta name="viewport" content="width=device-width, initial-scale=1.0">
      // <title>Special Discount Offer</title>
      // <style>
      // body {
      // font-family: Arial, sans-serif;
      // line-height: 1.6;
      // margin: 0;
      // padding: 20px;
      // background-color: #f9f9f9;
      // }
      // .container {
      // max-width: 600px;
      // margin: auto;
      // background: #fff;
      // padding: 20px;
      // border-radius: 8px;
      // box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      // }
      // h1 {
      // color: #333;
      // }
      // h2 {
      // color: #333;
      // }
      // p, strong {
      // color: #555;
      // }
      // a {
      // color: #007BFF;
      // text-decoration: none;
      // }
      // .footer {
      // margin-top: 20px;
      // font-size: 0.9em;
      // color: #777;
      // }
      // </style>
      // </head>
      // <body>
      // <div class="container">
      // <h1>Special 10% Discount Offer</h1>
      // <p>Hello {{customer_name}},</p>
      // <p>We noticed you left some items in your cart on {{order_date}}. To help you complete your purchase, we've created a special 10% discount coupon just for you!</p>
      // <p><strong>Coupon Code:</strong> {{coupon_code}}</p>
      // <p><strong>Valid Until:</strong> {{coupon_end_date}}</p>
      // <p>To apply this discount and complete your order, visit <a href="{{base_url}}/app/products">our store</a>.</p>
      // <p>We welcome you to our store anytime. If you need assistance or have any questions, please email us at <a href="mailto:{{contact_us_email}}">{{contact_us_email}}</a> or call <a href="tel:{{contact_us_phone}}">{{contact_us_phone}}</a>. We are happy to help!</p>
      // <div class="footer">
      // <p>Sincerely,<br><a href="{{base_url}}">Juffrou Anri Se Klaskamer</a></p>
      // <p><a href="{{facebook_link}}">Facebook</a></p>
      // </div>
      // </div>
      // </body>
      // </html>`,
      //         },
      //       };

//       var params = {
//         Template: {
//           TemplateName: "ProductOutdatedTemplate-v1_1",
//           SubjectPart:
//             "New Product Version Available: {{product_title}} — Juffrou Anri Se Klaskamer",
//           TextPart: `Product Update Available
// Hello {{customer_name}},

// Great news! A product you recently purchased, {{product_title}}, has been updated with a newer version.

// You can download the latest files using the link below:
// {{product_link_text}}

// If you need assistance or have any questions, please email us at {{contact_us_email}} or call {{contact_us_phone}}. We are happy to help!

// Sincerely,
// Juffrou Anri Se Klaskamer`,
//           HtmlPart: `<!DOCTYPE html>
// <html lang="en">
// <head>
// <meta charset="UTF-8">
// <meta name="viewport" content="width=device-width, initial-scale=1.0">
// <title>Product Update Available</title>
// <style>
// body {
// font-family: Arial, sans-serif;
// line-height: 1.6;
// margin: 0;
// padding: 20px;
// background-color: #f9f9f9;
// }
// .container {
// max-width: 600px;
// margin: auto;
// background: #fff;
// padding: 20px;
// border-radius: 8px;
// box-shadow: 0 2px 10px rgba(0,0,0,0.1);
// }
// h1 {
// color: #333;
// }
// p, strong {
// color: #555;
// }
// a {
// color: #007BFF;
// text-decoration: none;
// }
// .footer {
// margin-top: 20px;
// font-size: 0.9em;
// color: #777;
// }
// </style>
// </head>
// <body>
// <div class="container">
// <h1>New Version Available</h1>
// <p>Hello {{customer_name}},</p>
// <p>We wanted to let you know that a product you recently purchased, <strong>{{product_title}}</strong>, has been updated.</p>
// <p>You can download the newer version from your account here:</p>
// <p><strong>Download:</strong> <a href="{{product_link_text}}">{{product_link_text}}</a></p>
// <p>If you need assistance or have any questions, please email us at <a href="mailto:{{contact_us_email}}">{{contact_us_email}}</a> or call <a href="tel:{{contact_us_phone}}">{{contact_us_phone}}</a>. We are happy to help!</p>
// <div class="footer">
// <p>Sincerely,<br><a href="{{base_url}}">Juffrou Anri Se Klaskamer</a></p>
// <p><a href="{{facebook_link}}">Facebook</a></p>
// </div>
// </div>
// </body>
// </html>`,
//         },
//       };

      ses.createTemplate(params, function (err, data) {
        if (err) console.log(err, err.stack);
        else console.log(data);
      });
    },

    deleteEmailTemplate() {
      AWS.config.update({
        region: "af-south-1",
        accessKeyId: "",
        secretAccessKey: "",
      });
      var ses = new AWS.SES();
      var params = {
        TemplateName: "ProductsOrderQuotationTemplate-v1_1", // ProductsOrderPaidEFTTemplate
      };
      ses.deleteTemplate(params, function (err, data) {
        if (err) console.log(err, err.stack);
        else console.log(data);
      });
    },

    // async getUserGroups() {
    //   this.fetchedUserGroups = false;

    //   const user = await Auth.currentAuthenticatedUser().catch(() => {
    //     // eslint-disable-next-line @typescript-eslint/no-empty-function
    //   });

    //   if (user) {
    //     await API.post("cognitolambdaapi", "/item/listUserGroups", {
    //       body: {
    //         userPoolId: user.pool.userPoolId,
    //         userName: user.username,
    //       },
    //     })
    //       .then(async (cognitoListUserGroupsResponse) => {
    //         if (cognitoListUserGroupsResponse.list) {
    //           // console.log(
    //           //   "cognitoListUserGroupsResponse:",
    //           //   cognitoListUserGroupsResponse
    //           // );
    //           this.localUserGroups = JSON.parse(
    //             cognitoListUserGroupsResponse.list
    //           );
    //           store.commit("storeUserGroups", this.localUserGroups);
    //         }

    //         this.fetchedUserGroups = true;
    //       })
    //       .catch((error) => {
    //         // Log any errors
    //         console.log("error:", error);

    //         this.fetchedUserGroups = true;
    //       });
    //   }
    // },

    sessionWarningClose() {
      store.dispatch("signOut");
      this.dialogSessionWarning = false;
    },

    async sessionWarningUseHere() {
      this.newTeacherSessionToken = Date.now().toString();
      // console.log("this.newTeacherSessionToken:", this.newTeacherSessionToken);
      localStorage.setItem("teacherSessionToken", this.newTeacherSessionToken);

      await API.graphql({
        query: updateTeacherProfile,
        variables: {
          input: {
            id: this.syncedTeacherProfileModel.id,
            _version: this.syncedTeacherProfileModel._version,
            sessionToken: this.newTeacherSessionToken,
          },
        },
        authMode: this.authMode,
      });

      // await DataStore.save(
      //   TeacherProfile.copyOf(this.syncedTeacherProfileModel, (updateModel) => {
      //     updateModel.sessionToken = this.newTeacherSessionToken;
      //   }),
      // );

      // await API.graphql({
      //   query: updateTeacherProfile,
      //   variables: {
      //     input: {
      //       id: this.syncedTeacherProfileModel.id,
      //       _version: this.syncedTeacherProfileModel._version,
      //       sessionToken: this.newTeacherSessionToken,
      //     },
      //   },
      //   authMode: this.authMode,
      // });

      this.dialogSessionWarning = false;
    },

    async checkPayfastSubscription() {
      // console.log(
      //   "App.checkPayfastSubscription",
      //   this.teacherProfilePayfastSubscriptionToken
      // );
      if (!this.teacherProfilePayfastSubscriptionToken) {
        return;
      }

      let subscriptionStillValid = false;

      await API.get(
        "payfastsubscriptionapi",
        `/payfast/status/${this.teacherProfilePayfastSubscriptionToken}`,
      )
        .then(async (payfastStatusResponse) => {
          // console.log("payfastStatusResponse:", payfastStatusResponse);
          if (
            this.teacherProfilePayfastSubscriptionResponse !==
            payfastStatusResponse.success.data.response
          ) {
            await API.graphql({
              query: updateTeacherProfile,
              variables: {
                input: {
                  id: this.syncedTeacherProfileModel.id,
                  _version: this.syncedTeacherProfileModel._version,
                  payfastSubscriptionResponse:
                    typeof payfastStatusResponse.success.data.response ===
                    "object"
                      ? JSON.stringify(
                          payfastStatusResponse.success.data.response,
                        )
                      : null,
                },
              },
              authMode: this.authMode,
            });

            // await DataStore.save(
            //   TeacherProfile.copyOf(
            //     this.syncedTeacherProfileModel,
            //     (updateModel) => {
            //       updateModel.payfastSubscriptionResponse =
            //         typeof payfastStatusResponse.success.data.response ===
            //         "object"
            //           ? payfastStatusResponse.success.data.response
            //           : // {
            //             //     ...payfastStatusResponse.success.data.response,
            //             //     run_date: "2024-07-21T00:00:00+02:00",
            //             //   }
            //             null;
            //     },
            //   ),
            // );

            // await API.graphql({
            //   query: updateTeacherProfile,
            //   variables: {
            //     input: {
            //       id: this.syncedTeacherProfileModel.id,
            //       payfastSubscriptionResponse:
            //         typeof payfastStatusResponse.success.data.response ===
            //         "object"
            //           ? JSON.stringify(
            //               payfastStatusResponse.success.data.response,
            //             )
            //           : null,
            //     },
            //   },
            //   authMode: this.authMode,
            // });

            // store.commit(
            //   "storeTeacherProfilePayfastSubscriptionResponse",
            //   payfastStatusResponse.success.data.response
            // );
          }

          const user = await Auth.currentAuthenticatedUser().catch(() => {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
          });

          if (user) {
            if (payfastStatusResponse.success.data.response.run_date) {
              const runDate = new Date(
                payfastStatusResponse.success.data.response.run_date,
              );
              const currentDate = new Date();
              subscriptionStillValid = runDate > currentDate;
              // Compare the dates
              // console.log(
              //   "runDate:",
              //   runDate,
              //   "currentDate:",
              //   currentDate,
              //   "subscriptionStillValid:",
              //   subscriptionStillValid
              // );
              // console.log(
              //   "payfastStatusResponse.success.data:",
              //   payfastStatusResponse.success.data
              // );
            }

            if (
              payfastStatusResponse.success.data.response?.status_text ===
                "ACTIVE" ||
              subscriptionStillValid
            ) {
              if (!this.userGroups?.includes("teacher")) {
                // User is not part of the 'teacher' group
                // console.log('User is not a teacher');

                await API.post("cognitolambdaapi", "/item/addUserToGroup", {
                  body: {
                    groupName: "teacher",
                    userPoolId: user.pool.userPoolId,
                    userName: user.username,
                  },
                })
                  .then(() => {
                    let newUserGroups = this.userGroups
                      ? [...this.userGroups]
                      : [];
                    newUserGroups.push("teacher");
                    store.commit("storeUserGroups", newUserGroups);
                  })
                  .catch((error) => {
                    // Log any errors
                    console.log("error:", error);
                  });
              }
            }

            // REMOVE teacher role if subscription is not active

            // else if (this.localUserGroups.includes("teacher")) {
            //   await API.post("cognitolambdaapi", "/item/removeUserFromGroup", {
            //     body: {
            //       groupName: "teacher",
            //       userPoolId: user.pool.userPoolId,
            //       userName: user.username,
            //     },
            //   })
            //     .then(() => {
            //       let index = this.localUserGroups.indexOf("teacher");

            //       if (index !== -1) {
            //         this.localUserGroups.splice(index, 1);
            //       }

            //       // console.log("this.localUserGroups: ", this.localUserGroups);
            //       store.commit("storeUserGroups", this.localUserGroups);
            //     })
            //     .catch((error) => {
            //       // Log any errors
            //       console.log("error:", error);
            //     });
            // }
          }

          // console.log(payfastStatusResponse);
          store.commit("storeTeacherProfilePayfastSubscriptionResponse", {
            ...payfastStatusResponse.success.data.response,
            // run_date: "2024-07-21T00:00:00+02:00",
            subscriptionStillValid,
          });
        })
        .catch(async (error) => {
          store.commit(
            "storeTeacherProfilePayfastSubscriptionResponse",
            this.syncedTeacherProfileModel
              ? {
                  ...this.syncedTeacherProfileModel.payfastSubscriptionResponse,
                  subscriptionStillValid,
                }
              : null,
          );

          console.error(error.message);
        });
    },

    // async updateTeacherSmartProductSort(items) {
    //   try {
    //     console.log(
    //       "Warning: More than one TeacherProfile found for the user.",
    //     );

    //     const newSyncedTPM = items[0];

    //     const newSmartProductSort = {
    //       categories: [...(newSyncedTPM.smartProductSort?.categories || [])],
    //       grades: [...(newSyncedTPM.smartProductSort?.grades || [])],
    //     };

    //     for (let i = 1; i < items.length; i++) {
    //       const duplicateTPM = items[i];

    //       // Merge smartProductSort arrays
    //       newSmartProductSort.categories = [
    //         ...new Set([
    //           ...newSmartProductSort.categories,
    //           ...(duplicateTPM.smartProductSort?.categories || []),
    //         ]),
    //       ];
    //       newSmartProductSort.grades = [
    //         ...new Set([
    //           ...newSmartProductSort.grades,
    //           ...(duplicateTPM.smartProductSort?.grades || []),
    //         ]),
    //       ];

    //       // Delete the duplicate TeacherProfile (uncomment if needed, as in original)
    //       // await DataStore.delete(TeacherProfile, (tp) => tp.id("eq", duplicateTPM.id));

    //       // await API.graphql({
    //       //   query: deleteTeacherProfile,
    //       //   variables: { input: { id: duplicateTPM.id } },
    //       //   authMode: this.authMode,
    //       // });
    //     }

    //     await DataStore.save(
    //       TeacherProfile.copyOf(newSyncedTPM, (updateModel) => {
    //         updateModel.smartProductSort = newSmartProductSort;
    //       }),
    //     );

    //     // await API.graphql({
    //     //   query: updateTeacherProfile,
    //     //   variables: {
    //     //     input: {
    //     //       id: newSyncedTPM.id,
    //     //       _version: newSyncedTPM._version,
    //     //       smartProductSort: JSON.stringify(newSmartProductSort),
    //     //     },
    //     //   },
    //     //   authMode: this.authMode,
    //     // });

    //     this.updatedSmartProductSort = true;
    //   } catch (error) {
    //     console.log("updateTeacherSmartProductSort error:", error);
    //   }
    // },

    syncData() {
      // console.log("syncData");
      try {
        if (!this.teacherProductsOrderUpdateSub) {
          this.syncTeacherProductsOrders(this.syncedTeacherProfileModel.id);
        }
        if (!this.subscriptionExists("Announcement")) {
          this.syncAnnouncements(this.syncedTeacherProfileModel.id);
        }
      } catch (error) {
        console.log("syncData app.vue error:", error);
      }
    },

    // async syncTeacherProfile() {
    //   try {
    //     // console.log("syncTeacherProfile");
    //     const teacherProfileSubscription = DataStore.observeQuery(
    //       TeacherProfile,
    //     ).subscribe(
    //       async (snapshot) => {
    //         const { items, isSynced } = snapshot;

    //         if (
    //           items.length > 1 &&
    //           !router.currentRoute.path.includes("/admin") &&
    //           !this.updatedSmartProductSort
    //         ) {
    //           await this.updateTeacherSmartProductSort(items);
    //           return;
    //         }

    //         if (isSynced) {
    //           store.commit("storeSyncedTeacherProfile", true);
    //         } else {
    //           store.commit("storeSyncedTeacherProfile", false);
    //         }

    //         if (
    //           // isSynced &&
    //           items &&
    //           items.length > 0
    //           // &&
    //           // JSON.stringify(items[0]) !==
    //           //   JSON.stringify(this.syncedTeacherProfileModel)
    //         ) {
    //           const newSyncedTPM = items[0];
    //           // this.syncedTeacherProfileModel = newSyncedTPM;

    //           store.commit("storeTeacherProfileModel", newSyncedTPM);

    //           console.log(
    //             "syncTeacherProfile - syncing justBoughtProducts with smartProductSort categories",
    //             newSyncedTPM?.smartProductSort?.categories,
    //           );

    //           this.syncJustBoughtProducts(
    //             newSyncedTPM?.smartProductSort?.categories || [],
    //           );

    //           // console.log("newSyncedTPM:", newSyncedTPM);

    //           //HIDE-subscription-17Dec2024
    //           // if (!this.localUserGroups.includes("teacher")) {
    //           //   console.log("User is not a teacher");
    //           //   const cognitoUsername = newSyncedTPM.owner.split("::").pop();

    //           //   API.post("cognitolambdaapi", "/item/addUserToGroup", {
    //           //     body: {
    //           //       groupName: "teacher",
    //           //       userName: cognitoUsername,
    //           //     },
    //           //   })
    //           //     .then(() => {
    //           //       this.localUserGroups.push("teacher");
    //           //       console.log("this.localUserGroups: ", this.localUserGroups);
    //           //       store.commit("storeUserGroups", this.localUserGroups);
    //           //     })
    //           //     .catch((error) => {
    //           //       // Log any errors
    //           //       console.log("error:", error);
    //           //     });
    //           // } else {
    //           //   console.log("User is a teacher");
    //           // }

    //           if (newSyncedTPM.translateI18n > "") {
    //             const newLang = newSyncedTPM.translateI18n;
    //             // newSyncedTPM.language === "Afrikaans" ? "af" : "en";
    //             this.$vuetify.lang.current = newLang;
    //             this.$i18n.locale = newLang;
    //           }

    //           if (this.teacherProfileID !== newSyncedTPM.id) {
    //             store.commit("storeTeacherProfileID", newSyncedTPM.id);

    //             if (!this.previouslySyncedTeacherProfile) {
    //               this.previouslySyncedTeacherProfile = true;
    //               this.changedDatastorePredicate = true;

    //               if (this.datastoreReady && !this.changingDatastoreConfig) {
    //                 // console.log("syncTeacher - reconfigureDatastore");
    //                 await reconfigureDatastore();
    //                 this.changedDatastorePredicate = false;
    //               }
    //               return;
    //             }
    //           }

    //           if (
    //             !this.changingDatastoreConfig &&
    //             !this.teacherProductsOrderUpdateSub
    //             // !this.subscriptionExists("TeacherProductsOrder")
    //           ) {
    //             // console.log("syncData TeacherProfileSub");
    //             this.syncData();
    //           }

    //           if (
    //             !this.initiallySynced ||
    //             this.teacherProfilePayfastSubscriptionToken !==
    //               newSyncedTPM.payfastSubscriptionToken
    //           ) {
    //             store.commit(
    //               "storeTeacherProfilePayfastSubscriptionToken",
    //               newSyncedTPM.payfastSubscriptionToken,
    //             );
    //             this.checkPayfastSubscription();

    //             this.initiallySynced = true;
    //           }

    //           if (typeof newSyncedTPM._version === "string") {
    //             if (
    //               this.teacherProfileAppSyncVersion !== newSyncedTPM._version
    //             ) {
    //               store.commit(
    //                 "storeTeacherProfileAppSyncVersion",
    //                 newSyncedTPM._version,
    //               );
    //             }
    //           } else if (newSyncedTPM._version) {
    //             if (
    //               this.teacherProfileAppSyncVersion !==
    //               newSyncedTPM._version.toString()
    //             ) {
    //               store.commit(
    //                 "storeTeacherProfileAppSyncVersion",
    //                 newSyncedTPM._version.toString(),
    //               );
    //             }
    //           }

    //           if (this.teacherProfileProvince !== newSyncedTPM.province) {
    //             store.commit(
    //               "storeTeacherProfileProvince",
    //               newSyncedTPM.province,
    //             );
    //           }

    //           if (
    //             this.teacherProfileSchoolID !==
    //             newSyncedTPM.teacherProfileSchoolId
    //           ) {
    //             store.commit(
    //               "storeTeacherProfileSchoolID",
    //               newSyncedTPM.teacherProfileSchoolId,
    //             );
    //           }

    //           const currentSessionTokenGet = JSON.parse(
    //             localStorage.getItem("teacherSessionToken"),
    //           );
    //           const currentSessionToken = currentSessionTokenGet
    //             ? currentSessionTokenGet.toString()
    //             : "";

    //           if (typeof newSyncedTPM.sessionToken === "string") {
    //             if (currentSessionToken === "") {
    //               this.newTeacherSessionToken = Date.now().toString();

    //               localStorage.setItem(
    //                 "teacherSessionToken",
    //                 this.newTeacherSessionToken,
    //               );

    //               await DataStore.save(
    //                 TeacherProfile.copyOf(newSyncedTPM, (updateModel) => {
    //                   updateModel.sessionToken = this.newTeacherSessionToken;
    //                 }),
    //               );

    //               store.commit("storeJustLoggedIn", true);
    //             } else if (newSyncedTPM.sessionToken !== currentSessionToken) {
    //               this.dialogSessionWarning = true;
    //             }
    //           } else if (this.newTeacherSessionToken === "") {
    //             this.newTeacherSessionToken = Date.now().toString();

    //             localStorage.setItem(
    //               "teacherSessionToken",
    //               this.newTeacherSessionToken,
    //             );

    //             await DataStore.save(
    //               TeacherProfile.copyOf(newSyncedTPM, (updateModel) => {
    //                 updateModel.sessionToken = this.newTeacherSessionToken;
    //               }),
    //             );

    //             store.commit("storeJustLoggedIn", true);
    //           }

    //           if (
    //             newSyncedTPM.payfastSubscriptionPackage
    //               ?.proRataPaymentSuccessful === true &&
    //             newSyncedTPM.sessionToken === currentSessionToken
    //           ) {
    //             const newPackage = _.cloneDeep(
    //               newSyncedTPM.payfastSubscriptionPackage,
    //             );
    //             delete newPackage.proRataPaymentSuccessful;

    //             const recurring_amount = (
    //               (newPackage.grades.length > 1
    //                 ? newPackage.perGrade * (newPackage.grades.length - 1) +
    //                   newPackage.amount
    //                 : newPackage.amount) * 100
    //             ).toFixed(0);

    //             const bodyToUpdate = {
    //               teacherProfileID: newSyncedTPM.id,
    //               package: newPackage,
    //               payfastSubscriptionToken:
    //                 newSyncedTPM.payfastSubscriptionToken,
    //               recurring_amount: recurring_amount,
    //             };

    //             await this.updatePayfastSubscription(bodyToUpdate);
    //           }
    //         }
    //         // else if (
    //         //   this.teacherProfileCreatable &&
    //         //   items?.length === 0 &&
    //         //   isSynced
    //         // ) {
    //         //   // console.log("CREATE NEW TEACHER PROFILE -APP.vue");

    //         //   this.createdNewTeacherProfile = true;
    //         //   await DataStore.save(
    //         //     new TeacherProfile({
    //         //       title: "",
    //         //       name: "",
    //         //       surname: "",
    //         //       language: "",
    //         //       province: "",
    //         //       daysPerWeek: 0,
    //         //       status: "Active",
    //         //       TeacherSchedules: [],
    //         //       TeacherClassSubjects: [],
    //         //       type: "TeacherProfile",
    //         //     })
    //         //   );
    //         // }
    //       },
    //       (error) => {
    //         console.log("syncTeacherProfile error:", error);
    //       },
    //     );

    //     store.commit("addSubscription", {
    //       model: "TeacherProfile",
    //       sub: teacherProfileSubscription,
    //     });
    //   } catch (error) {
    //     console.log(error);
    //   }
    // },

    // async handleTeacherProfileUpdate(newSyncedTPM) {
    //   if (
    //     JSON.stringify(newSyncedTPM) !==
    //     JSON.stringify(this.syncedTeacherProfileModel)
    //   ) {
    //     store.commit("storeTeacherProfileModel", newSyncedTPM);

    //     console.log("newSyncedTPM:", newSyncedTPM);

    //     if (newSyncedTPM.translateI18n > "") {
    //       const newLang = newSyncedTPM.translateI18n;
    //       this.$vuetify.lang.current = newLang;
    //       this.$i18n.locale = newLang;
    //     }

    //     if (
    //       !this.changingDatastoreConfig &&
    //       !this.teacherProductsOrderUpdateSub
    //     ) {
    //       this.syncData();
    //     }

    //     if (this.teacherProfileID !== newSyncedTPM.id) {
    //       store.commit("storeTeacherProfileID", newSyncedTPM.id);

    //       if (!this.previouslySyncedTeacherProfile) {
    //         this.previouslySyncedTeacherProfile = true;
    //         this.changedDatastorePredicate = true;

    //         if (this.datastoreReady && !this.changingDatastoreConfig) {
    //           console.log("syncTeacher - reconfigureDatastore");
    //           await reconfigureDatastore();
    //           this.changedDatastorePredicate = false;
    //         }
    //         return;
    //       }
    //     }

    //     if (
    //       !this.initiallySynced ||
    //       this.teacherProfilePayfastSubscriptionToken !==
    //         newSyncedTPM.payfastSubscriptionToken
    //     ) {
    //       store.commit(
    //         "storeTeacherProfilePayfastSubscriptionToken",
    //         newSyncedTPM.payfastSubscriptionToken,
    //       );
    //       this.checkPayfastSubscription();

    //       this.initiallySynced = true;
    //     }

    //     if (typeof newSyncedTPM._version === "string") {
    //       if (this.teacherProfileAppSyncVersion !== newSyncedTPM._version) {
    //         store.commit(
    //           "storeTeacherProfileAppSyncVersion",
    //           newSyncedTPM._version,
    //         );
    //       }
    //     } else if (newSyncedTPM._version) {
    //       if (
    //         this.teacherProfileAppSyncVersion !==
    //         newSyncedTPM._version.toString()
    //       ) {
    //         store.commit(
    //           "storeTeacherProfileAppSyncVersion",
    //           newSyncedTPM._version.toString(),
    //         );
    //       }
    //     }

    //     if (this.teacherProfileProvince !== newSyncedTPM.province) {
    //       store.commit("storeTeacherProfileProvince", newSyncedTPM.province);
    //     }

    //     if (
    //       this.teacherProfileSchoolID !== newSyncedTPM.teacherProfileSchoolId
    //     ) {
    //       store.commit(
    //         "storeTeacherProfileSchoolID",
    //         newSyncedTPM.teacherProfileSchoolId,
    //       );
    //     }

    //     const currentSessionTokenGet = JSON.parse(
    //       localStorage.getItem("teacherSessionToken"),
    //     );
    //     const currentSessionToken = currentSessionTokenGet
    //       ? currentSessionTokenGet.toString()
    //       : "";

    //     if (typeof newSyncedTPM.sessionToken === "string") {
    //       if (currentSessionToken === "") {
    //         this.newTeacherSessionToken = Date.now().toString();

    //         localStorage.setItem(
    //           "teacherSessionToken",
    //           this.newTeacherSessionToken,
    //         );

    //         API.graphql({
    //           query: updateTeacherProfile,
    //           variables: {
    //             input: {
    //               id: newSyncedTPM.id,
    //               _version: newSyncedTPM._version,
    //               sessionToken: this.newTeacherSessionToken,
    //             },
    //           },
    //           authMode: this.authMode,
    //         });

    //         store.commit("storeJustLoggedIn", true);
    //       } else if (newSyncedTPM.sessionToken !== currentSessionToken) {
    //         this.dialogSessionWarning = true;
    //       }
    //     } else if (this.newTeacherSessionToken === "") {
    //       this.newTeacherSessionToken = Date.now().toString();

    //       localStorage.setItem(
    //         "teacherSessionToken",
    //         this.newTeacherSessionToken,
    //       );

    //       API.graphql({
    //         query: updateTeacherProfile,
    //         variables: {
    //           input: {
    //             id: newSyncedTPM.id,
    //             _version: newSyncedTPM._version,
    //             sessionToken: this.newTeacherSessionToken,
    //           },
    //         },
    //         authMode: this.authMode,
    //       });

    //       store.commit("storeJustLoggedIn", true);
    //     }

    //     if (
    //       newSyncedTPM.payfastSubscriptionPackage?.proRataPaymentSuccessful ===
    //         true &&
    //       newSyncedTPM.sessionToken === currentSessionToken
    //     ) {
    //       const newPackage = _.cloneDeep(
    //         newSyncedTPM.payfastSubscriptionPackage,
    //       );
    //       delete newPackage.proRataPaymentSuccessful;

    //       const recurring_amount = (
    //         (newPackage.grades.length > 1
    //           ? newPackage.perGrade * (newPackage.grades.length - 1) +
    //             newPackage.amount
    //           : newPackage.amount) * 100
    //       ).toFixed(0);

    //       const bodyToUpdate = {
    //         teacherProfileID: newSyncedTPM.id,
    //         package: newPackage,
    //         payfastSubscriptionToken: newSyncedTPM.payfastSubscriptionToken,
    //         recurring_amount: recurring_amount,
    //       };

    //       this.updatePayfastSubscription(bodyToUpdate);
    //     }
    //   }
    // },

    handleTeacherProductsOrders(items, isSynced = true) {
      // console.log("handleTeacherProductsOrders - items:", items);

      // let updatedPendingOrder = false;
      if (items?.length > 0) {
        store.dispatch("storeSyncedTeacherProductsOrderModels", items);

        // const pendingTeacherProductsOrder = items.find(
        //   (product) => product.orderStatus === "PENDING",
        // );

        // const pendingOrderProducts = pendingTeacherProductsOrder?.orderProducts;

        // if (pendingOrderProducts?.length > 0) {
        //   const uniqueOrderProducts = [
        //     ...new Map(
        //       pendingOrderProducts.map((product) => [product.id, product]),
        //     ).values(),
        //   ];

        //   if (
        //     JSON.stringify(uniqueOrderProducts) !==
        //     JSON.stringify(pendingOrderProducts)
        //   ) {
        //     API.graphql({
        //       query: updateTeacherProductsOrder,
        //       variables: {
        //         input: {
        //           id: pendingTeacherProductsOrder.id,
        //           _version: pendingTeacherProductsOrder._version,
        //           // updatedAtMonth: new Date().toISOString().slice(0, 7),
        //           orderProducts: JSON.stringify(uniqueOrderProducts),
        //         },
        //       },
        //       authMode: this.authMode,
        //     });
        //     updatedPendingOrder = true;
        //   }
        // }
      } else {
        store.dispatch("storeSyncedTeacherProductsOrderModels", null);
      }

      if (
        isSynced
        //  && !updatedPendingOrder
      ) {
        store.commit("storeSyncedTeacherProductsOrders", true);

        if (this.userSignedIn) {
          store.dispatch("syncAdMetaToCart");
        }
      } else {
        store.commit("storeSyncedTeacherProductsOrders", false);
      }
    },

    // async syncTeacherProfile() {
    //   try {
    //     const user = await Auth.currentAuthenticatedUser().catch(() => {
    //       // eslint-disable-next-line @typescript-eslint/no-empty-function
    //     });

    //     const cognitoUsername = `${user.attributes.sub}::${user.username}`;
    //     // console.log("cognitoUsername:", cognitoUsername);
    //     console.log("syncTeacherProfile");
    //     const result = await API.graphql({
    //       query: teacherProfilesByOwner,
    //       variables: {
    //         owner: cognitoUsername,
    //         filter: { _deleted: { ne: true } },
    //       },
    //       authMode: this.authMode,
    //     });

    //     let rawItems = result.data.teacherProfilesByOwner.items;
    //     let items = this.parseGraphqlResult(rawItems);

    //     store.commit("storeSyncedTeacherProfile", true);

    //     if (items.length > 0) {
    //       await this.handleTeacherProfileUpdate(items[0]);
    //     } else {
    //       store.commit("storeTeacherProfileModel", null);
    //     }

    //     if (
    //       items.length > 1 &&
    //       !router.currentRoute.path.includes("/admin") &&
    //       !this.updatedSmartProductSort
    //     ) {
    //       await this.updateTeacherSmartProductSort(items);

    //       const refreshedResult = await API.graphql({
    //         query: teacherProfilesByOwner,
    //         variables: {
    //           owner: cognitoUsername,
    //           filter: { _deleted: { ne: true } },
    //         },
    //         authMode: this.authMode,
    //       });

    //       rawItems = refreshedResult.data.teacherProfilesByOwner.items;
    //       items = this.parseGraphqlResult(rawItems);
    //     }

    //     this.teacherProfileSubscription = API.graphql({
    //       query: onUpdateTeacherProfile,
    //       variables: {
    //         filter: { _deleted: { ne: true } },
    //       },
    //       authMode: this.authMode,
    //     }).subscribe({
    //       next: ({ value }) => {
    //         const updated = value.data.onUpdateTeacherProfile;
    //         if (updated) {
    //           const parsedItem = this.parseGraphqlResult(updated);
    //           this.handleTeacherProfileUpdate(parsedItem);
    //         }
    //       },
    //       error: (error) => {
    //         console.log("syncTeacherProfile error:", error);
    //       },
    //     });

    //     this.onCreateTeacherProfileSub = API.graphql({
    //       query: onCreateTeacherProfile,
    //       authMode: this.authMode,
    //     }).subscribe({
    //       next: ({ value }) => {
    //         const created = value.data.onCreateTeacherProfile;
    //         if (created) {
    //           const parsedItem = this.parseGraphqlResult(created);
    //           this.handleTeacherProfileUpdate(parsedItem);
    //         }
    //       },
    //       error: (error) => {
    //         console.log("syncTeacherProfile error:", error);
    //       },
    //     });

    //     this.onDeleteTeacherProfileSub = API.graphql({
    //       query: onDeleteTeacherProfile,
    //       authMode: this.authMode,
    //     }).subscribe({
    //       next: () => {
    //         store.commit("storeTeacherProfileModel", null);
    //         store.commit("storeSyncedTeacherProfile", true);
    //       },
    //       error: (error) => {
    //         console.log("syncTeacherProfile error:", error);
    //       },
    //     });

    //     // Note: No addSubscription to store, as we're using AppSync now
    //   } catch (error) {
    //     console.log(error);
    //   }
    // },

    async syncTeacherProductsOrders(teacherProfileIDProp) {
      // console.log("teacherProfileIDProp:", teacherProfileIDProp);
      try {
        let allItems = [];
        let nextToken = null;
        // let page = 1;

        do {
          const result = await API.graphql({
            query: teacherProductsOrdersByTeacherprofileID,
            variables: {
              teacherprofileID: teacherProfileIDProp,
              filter: { _deleted: { ne: true } },
              nextToken: nextToken, // will be null on first call
            },
            authMode: this.authMode,
          });

          const pageItems =
            result.data.teacherProductsOrdersByTeacherprofileID.items;
          const pageNextToken =
            result.data.teacherProductsOrdersByTeacherprofileID.nextToken;

          // Add current page items to the total
          allItems = [...allItems, ...pageItems];

          //   console.log(
          //     `Page ${page} fetched - items: ${
          //       pageItems.length
          //     }, nextToken: ${!!pageNextToken}`,
          //   );
          //  page++;

          // Update nextToken for the next iteration
          nextToken = pageNextToken;
        } while (nextToken); // Continue until nextToken is null/undefined

        // Parse all collected items
        const items = this.parseGraphqlResult(allItems);

        // console.log(
        //   "syncTeacherProductsOrders - total items:",
        //   items.length,
        //   teacherProfileIDProp,
        // );

        // Update local state / store with all orders
        this.handleTeacherProductsOrders(items);

        this.teacherProductsOrderCreateSub = API.graphql({
          query: onCreateTeacherProductsOrder,
          authMode: this.authMode,
        }).subscribe({
          next: ({ value }) => {
            const created = this.parseGraphqlResult(
              value.data.onCreateTeacherProductsOrder,
            );

            if (created && created.teacherprofileID === teacherProfileIDProp) {
              const currentItems =
                store.state.syncedTeacherProductsOrderModels || [];
              const newItems = [...currentItems, created];
              this.handleTeacherProductsOrders(newItems);
              store.dispatch("storeSyncedTeacherProductsOrderModels", newItems);
            }
          },
          error: (error) => {
            console.log("syncTeacherProductsOrders error:", error);
          },
        });

        this.teacherProductsOrderUpdateSub = API.graphql({
          query: onUpdateTeacherProductsOrder,
          variables: {
            filter: { _deleted: { ne: true } },
          },
          authMode: this.authMode,
        }).subscribe({
          next: ({ value }) => {
            const updated = this.parseGraphqlResult(
              value.data.onUpdateTeacherProductsOrder,
            );
            console.log("Received TeacherProductsOrder update:", updated);
            if (updated && updated.teacherprofileID === teacherProfileIDProp) {
              const currentItems =
                store.state.syncedTeacherProductsOrderModels || [];
              const item = currentItems.find((i) => i.id === updated.id);
              // console.log("Index of updated order:", index);
              if (item) {
                const newItems = _.uniqBy(
                  currentItems.map((i) => (i.id === updated.id ? updated : i)),
                  "id",
                );

                this.handleTeacherProductsOrders(newItems);
                store.dispatch(
                  "storeSyncedTeacherProductsOrderModels",
                  newItems,
                );
              }
            }
          },
          error: (error) => {
            console.log("syncTeacherProductsOrders error:", error);
          },
        });

        this.teacherProductsOrderDeleteSub = API.graphql({
          query: onDeleteTeacherProductsOrder,
          authMode: this.authMode,
        }).subscribe({
          next: ({ value }) => {
            const deleted = value.data.onDeleteTeacherProductsOrder;
            if (deleted && deleted.teacherprofileID === teacherProfileIDProp) {
              const currentItems =
                store.state.syncedTeacherProductsOrderModels || [];
              const newItems = currentItems.filter((i) => i.id !== deleted.id);
              this.handleTeacherProductsOrders(newItems);
              store.dispatch("storeSyncedTeacherProductsOrderModels", newItems);
            }
          },
          error: (error) => {
            console.log("syncTeacherProductsOrders error:", error);
          },
        });
      } catch (error) {
        console.error("syncTeacherProductsOrders error:", error);
      }
    },

    async syncAnnouncements(teacherProfileID) {
      try {
        const announcementSubscription = DataStore.observeQuery(
          Announcement,
          (a) =>
            a.and((a) => [
              a.or((a) => [
                a.sendTo("contains", teacherProfileID),
                a.sendTo("contains", "ALL"),
              ]),
              a.readBy("notContains", teacherProfileID),
            ]),
        ).subscribe(
          async (snapshot) => {
            const { items, isSynced } = snapshot;
            if (items.length > 0) {
              store.commit("storeNotReadAnnouncements", items);
            } else {
              store.commit("storeNotReadAnnouncements", null);
            }
          },
          (error) => {
            console.log(error);
          },
        );

        store.commit("addSubscription", {
          model: "Announcement",
          sub: announcementSubscription,
        });
      } catch (error) {
        console.log("syncAnnouncements error:", error);
      }
    },

    async updatePayfastSubscription(bodyToUpdate) {
      try {
        await API.post("payfastsubscriptionapi", "/payfast/update", {
          body: bodyToUpdate,
        })
          .catch((error) => {
            // Log any errors
            console.log("error:", error);

            // DataStore.save(
            //   TeacherProfile.copyOf(
            //     this.syncedTeacherProfileModel,
            //     (updateModel) => {
            //       updateModel.payfastSubscriptionPackage =
            //         typeof this.syncedTeacherProfileModel
            //           .payfastSubscriptionPackage === "object"
            //           ? {
            //               ...this.syncedTeacherProfileModel
            //                 .payfastSubscriptionPackage,
            //               pendingPackage: bodyToUpdate.package,
            //             }
            //           : { pendingPackage: bodyToUpdate.package };
            //     }
            //   )
            // );
          })
          .then(async (response) => {
            // console.log("response:", response);
            // console.log(
            //   "response.success:",
            //   response.success ? "true" : "false"
            // );

            if (
              response?.success
              //  && (!amountToPay || amountToPay <= 0)
            ) {
              await API.graphql({
                query: updateTeacherProfile,
                variables: {
                  input: {
                    id: this.syncedTeacherProfileModel.id,
                    _version: this.syncedTeacherProfileModel._version,
                    payfastSubscriptionPackage: JSON.stringify(
                      bodyToUpdate.package,
                    ),
                  },
                },
                authMode: this.authMode,
              });

              // DataStore.save(
              //   TeacherProfile.copyOf(
              //     this.syncedTeacherProfileModel,
              //     (updateModel) => {
              //       updateModel.payfastSubscriptionPackage =
              //         bodyToUpdate.package;
              //     },
              //   ),
              // );
            }
            //  else {
            //   DataStore.save(
            //     TeacherProfile.copyOf(
            //       this.syncedTeacherProfileModel,
            //       (updateModel) => {
            //         updateModel.payfastSubscriptionPackage =
            //           typeof this.syncedTeacherProfileModel
            //             .payfastSubscriptionPackage === "object"
            //             ? {
            //                 ...this.syncedTeacherProfileModel
            //                   .payfastSubscriptionPackage,
            //                 pendingPackage: bodyToUpdate.package,
            //               }
            //             : { pendingPackage: bodyToUpdate.package };
            //       }
            //     )
            //   );
            // }

            this.checkPayfastSubscription();
          });
      } catch (error) {
        console.log(error);
      }
    },
  },

  beforeDestroy() {
    // if (this.teacherProfileSubscription)
    //   this.teacherProfileSubscription.unsubscribe();
    // if (this.onCreateTeacherProfileSub)
    //   this.onCreateTeacherProfileSub.unsubscribe();
    // if (this.onDeleteTeacherProfileSub)
    //   this.onDeleteTeacherProfileSub.unsubscribe();
    if (this.teacherProductsOrderCreateSub)
      this.teacherProductsOrderCreateSub.unsubscribe();
    if (this.teacherProductsOrderUpdateSub)
      this.teacherProductsOrderUpdateSub.unsubscribe();
    if (this.teacherProductsOrderDeleteSub)
      this.teacherProductsOrderDeleteSub.unsubscribe();
    store.commit("removeSubscriptions", [
      "Announcement", // Keep only Announcement, as it's unchanged
    ]);
  },
});
</script>

<style lang="scss" scoped>
.snackbar-click-wrapper {
  /* Vuetify 2 snackbar content padding is 8px 16px */
  margin: -8px -24px -8px -16px;
  padding: 4px 12px;
  cursor: pointer;
}
</style>
